import React from "react";

import { useNavigate, useOutletContext, useLocation } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { useAuth0 } from "@auth0/auth0-react";

import DashboardActionCard from "./DashboardActionCard";

import DashboardLoanTermsCard from "./DashboardLoanTermsCard";

import DashboardRenovationCard from "./DashboardRenovationCard";

import DashboardExternalRenovationCard from "./DashboardExternalRenovationCard";

import DashboardDraftProgressCard from "./DashboardDraftProgressCard";

import DashboardDossierOverviewCard from "./DashboardDossierOverviewCard";

import OssSituationModal from "./OssSituationModal";

import FirstLoginInfoModal from "./FirstLoginInfoModal";



import {

  getDossierDetail,

  prepareDossierDownload,

  downloadDossierZip,

  deleteDossier,

} from "../dossier/steps/homebuyerDossierApi";



import { mapDossierDetailToDraft } from "../dossier/steps/mapDossierDetailtoDraft";



import "./HomebuyerDashboard.css";



const API_BASE = process.env.REACT_APP_API_BASE_URL || "";



const DOSSIER_STORAGE_KEY = "legup_hb_dossier_draft";



const DOSSIER_META_STORAGE_KEY = "legup_hb_dossier_meta";

function buildScopedStorageKey(baseKey, ownerId) {
  return ownerId ? `${baseKey}:${encodeURIComponent(ownerId)}` : null;
}

function getSameDossierLocalData(localDraft, serverDossierId) {
  if (!localDraft || !serverDossierId) return null;
  return String(localDraft.dossier_id || "") === String(serverDossierId)
    ? localDraft
    : null;
}







function hasLoanTerms(summary = {}) {

  return Boolean(

    summary?.has_agreed_lender_result ||

    summary?.has_lender_result ||

    summary?.loan_decision === "APPROVED" ||

    summary?.lender_result_status === "APPROVED" ||

    summary?.approved_total_loan_amount ||

    summary?.agreed_total_loan_amount ||

    summary?.estimated_monthly_payment,

  );

}






export default function HomebuyerDashboard() {

  const navigate = useNavigate();



  const location = useLocation();



  const { account, profile, dashboardSummary, refreshMe } = useOutletContext();



  const { t } = useTranslation("homebuyer-dashboard");



  const { getAccessTokenSilently, user: auth0User } = useAuth0();

  // Must match HomebuyerDossierLayout.jsx: user-scoped storage, never a global draft.
  const storageOwnerId = auth0User?.sub || account?.email || "";
  const dossierStorageKey = React.useMemo(
    () => buildScopedStorageKey(DOSSIER_STORAGE_KEY, storageOwnerId),
    [storageOwnerId],
  );
  const dossierMetaStorageKey = React.useMemo(
    () => buildScopedStorageKey(DOSSIER_META_STORAGE_KEY, storageOwnerId),
    [storageOwnerId],
  );







  const summary = dashboardSummary || {};



  const firstName = account?.first_name || "User";






  // Do not show a cached progress count while /api/homeowner/me is revalidated.
  const [lastRefreshedDashboardKey, setLastRefreshedDashboardKey] =
    React.useState(null);
  const isRefreshingDashboard = lastRefreshedDashboardKey !== location.key;

  const refreshMeRef = React.useRef(refreshMe);



  React.useEffect(() => {

    refreshMeRef.current = refreshMe;

  }, [refreshMe]);



  React.useEffect(() => {

    let active = true;



    async function refreshDashboard() {

      try {

        await refreshMeRef.current?.();

      } catch (error) {

        if (!active) {

          return;

        }



        console.error("Could not refresh homebuyer dashboard:", error);

      } finally {
        if (active) setLastRefreshedDashboardKey(location.key);
      }

    }



    refreshDashboard();



    return () => {

      active = false;

    };

  }, [location.key]);






  const [ossSituationModalOpen, setOssSituationModalOpen] =

    React.useState(false);



  const [draftStorageRevision, setDraftStorageRevision] = React.useState(0);



  const [isDeletingDraft, setIsDeletingDraft] = React.useState(false);

  const [isDeletingReadyDossier, setIsDeletingReadyDossier] = React.useState(false);



  const [isDownloadingDossier, setIsDownloadingDossier] = React.useState(false);






  const dashboardRef = React.useRef(null);



  const underlayRef = React.useRef(null);






  const [firstLoginModalOpen, setFirstLoginModalOpen] = React.useState(false);



  const [firstLoginModalClosing, setFirstLoginModalClosing] =

    React.useState(false);



  const [dashboardLockedHeight, setDashboardLockedHeight] =

    React.useState(null);



  const firstLoginSeenRequestRef = React.useRef(false);



  const firstLoginCloseTimerRef = React.useRef(null);



  const firstLoginAutoOpenedRef = React.useRef(false);






  const savedDraftMeta = React.useMemo(() => {
    if (!dossierMetaStorageKey) return null;

    try {

      const raw = localStorage.getItem(dossierMetaStorageKey);



      return raw ? JSON.parse(raw) : null;

    } catch {

      return null;

    }

  }, [draftStorageRevision, dossierMetaStorageKey]);



  const savedDraft = React.useMemo(() => {
    if (!dossierStorageKey) return null;

    try {

      const raw = localStorage.getItem(dossierStorageKey);



      return raw ? JSON.parse(raw) : null;

    } catch {

      return null;

    }

  }, [draftStorageRevision, dossierStorageKey]);






  const hasDossier = Boolean(summary?.has_dossier);



  const isDraftDossier = Boolean(summary?.has_draft_dossier);



  const isReadyOrExportedDossier = Boolean(

    summary?.has_submitted_dossier ||

    summary?.has_ready_for_export_dossier ||

    summary?.has_exported_dossier,

  );



  const hasOssConnection = Boolean(summary?.has_oss_connection);



  const hasExternalRenovation = Boolean(summary?.has_external_renovation);



  const isLenderManualDossier = summary?.dossier_origin === "LENDER_MANUAL";






  const hasLocalDraftOnly =

    !hasDossier &&

    !isDraftDossier &&

    !isReadyOrExportedDossier &&

    Boolean(savedDraftMeta && savedDraft);






  const hasFinalLoanTerms = hasLoanTerms(summary);



  const hasAgreedLenderResult = Boolean(

    summary?.has_agreed_lender_result ||

    (summary?.has_lender_result && summary?.loan_decision === "APPROVED"),

  );






  React.useEffect(() => {

    if (!profile) {

      return;

    }



    if (firstLoginAutoOpenedRef.current) {

      return;

    }



    if (profile?.has_seen_first_login_modal === false) {

      firstLoginAutoOpenedRef.current = true;



      setFirstLoginModalOpen(true);



      return;

    }



    firstLoginAutoOpenedRef.current = true;

  }, [profile, profile?.has_seen_first_login_modal]);






  const continueDraftPath =

    summary?.current_step_path ||

    savedDraftMeta?.current_step_path ||

    "/hb/dossier/form/personal-info";






  // Dashboard must validate the same *canonical saved dossier* as the wizard.
  // The /me completed_steps may be stale or reflect filled rather than valid inputs.
  const [verifiedProgress, setVerifiedProgress] = React.useState({
    dossierId: null,
    draft: null,
    loading: true,
  });
  React.useEffect(() => {
    const dossierId = summary?.dossier_status === "DRAFT" ? summary?.dossier_id : null;
    if (!dossierId || isLenderManualDossier || isRefreshingDashboard) return undefined;
    let cancelled = false;
    setVerifiedProgress({ dossierId, draft: null, loading: true });
    (async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        const dossier = await getDossierDetail({ accessToken, dossierId });
        if (!cancelled) {
          setVerifiedProgress({ dossierId, draft: mapDossierDetailToDraft(dossier), loading: false });
        }
      } catch (error) {
        console.error("Could not validate saved dossier progress:", error);
        if (!cancelled) setVerifiedProgress({ dossierId, draft: null, loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, [summary?.dossier_id, summary?.dossier_status, summary?.last_update, isLenderManualDossier, isRefreshingDashboard, location.key, getAccessTokenSilently]);

  const draftMetaForCard =

    isDraftDossier && !isLenderManualDossier

      ? {

          dossier_id: summary?.dossier_id || null,



          dossier_code: summary?.dossier_code || null,



          status: summary?.dossier_status || "DRAFT",



          current_step_key:

            {

              PERSONAL: "personal-info",



              FINANCIAL: "financial-info",



              DOCUMENTS: "documents",



              EXTRACTION: "extracted-data",



              PROPERTY: "property-information",



              REVIEW: "review-download",

            }[summary?.current_stage] || null,



          current_step_label: summary?.current_stage_label || null,



          current_step_path: summary?.current_step_path || continueDraftPath,



          updated_at: summary?.last_update || null,



          // Persisted DossierStepStatus rows; do not recompute from cached form fields.
          progress_source: "verified",
          progress_revalidating: isRefreshingDashboard || (verifiedProgress.loading || !verifiedProgress.draft) ||
            String(verifiedProgress.dossierId || "") !== String(summary?.dossier_id || ""),
          completed_steps: summary?.completed_steps || {},

        }

      : savedDraftMeta;






  const draftDataForCard =
    isDraftDossier && !isLenderManualDossier
      ? (String(verifiedProgress.dossierId || "") === String(summary?.dossier_id || "")
          ? verifiedProgress.draft
          : null)
      : savedDraft;

  const handleDeleteDraft = React.useCallback(async () => {

    if (isLenderManualDossier) {

      return;

    }



    const dossierId =

      summary?.dossier_id ||

      draftDataForCard?.dossier_id ||

      savedDraftMeta?.dossier_id ||

      null;






    if (!dossierId) {

      if (dossierStorageKey) localStorage.removeItem(dossierStorageKey);



      if (dossierMetaStorageKey) localStorage.removeItem(dossierMetaStorageKey);



      setDraftStorageRevision((previous) => previous + 1);



      navigate("/hb/dashboard", {

        replace: true,

      });



      return;

    }






    try {

      setIsDeletingDraft(true);



      const accessToken = await getAccessTokenSilently();



      await deleteDossier({

        dossierId,



        accessToken,

      });



      if (dossierStorageKey) localStorage.removeItem(dossierStorageKey);



      if (dossierMetaStorageKey) localStorage.removeItem(dossierMetaStorageKey);



      setDraftStorageRevision((previous) => previous + 1);



      await refreshMe?.();



      navigate("/hb/dashboard", {

        replace: true,



        state: {

          draftDeleted: true,



          ts: Date.now(),

        },

      });

    } catch (error) {

      console.error("Could not delete dossier draft:", error);

    } finally {

      setIsDeletingDraft(false);

    }

  }, [

    isLenderManualDossier,



    summary?.dossier_id,



    draftDataForCard?.dossier_id,



    savedDraftMeta?.dossier_id,



    getAccessTokenSilently,



    refreshMe,



    navigate,

    dossierStorageKey,
    dossierMetaStorageKey,
  ]);






  // Delete a completed-but-unlinked dossier. The overview card handles
  // confirmation and displays any error thrown by this callback.
  const handleDeleteReadyDossier = React.useCallback(async () => {
    if (
      !summary?.dossier_id ||
      summary?.dossier_status !== "READY_FOR_EXPORT" ||
      summary?.can_delete_dossier !== true ||
      isLenderManualDossier
    ) {
      throw new Error(
        t("cards.completedDossier.delete.unavailable", "This dossier cannot be deleted."),
      );
    }

    setIsDeletingReadyDossier(true);
    try {
      const accessToken = await getAccessTokenSilently();
      await deleteDossier({ dossierId: summary.dossier_id, accessToken });

      if (dossierStorageKey) localStorage.removeItem(dossierStorageKey);
      if (dossierMetaStorageKey) localStorage.removeItem(dossierMetaStorageKey);
      setDraftStorageRevision((previous) => previous + 1);
      try {
        await refreshMe?.();
      } catch (refreshError) {
        // The dossier has already been deleted. Do not turn a refresh
        // failure into a misleading "deletion failed" message.
        console.error("Could not refresh dashboard after dossier deletion:", refreshError);
      }

      navigate("/hb/dashboard", {
        replace: true,
        state: { dossierDeleted: true, ts: Date.now() },
      });
    } catch (error) {
      console.error("Could not delete ready-for-export dossier:", error);
      throw error; // Keep the confirmation modal open with the API error.
    } finally {
      setIsDeletingReadyDossier(false);
    }
  }, [
    summary?.dossier_id,
    summary?.dossier_status,
    summary?.can_delete_dossier,
    isLenderManualDossier,
    getAccessTokenSilently,
    refreshMe,
    navigate,
    t,
    dossierStorageKey,
    dossierMetaStorageKey,
  ]);

  const showDraftState =

    !isLenderManualDossier && (isDraftDossier || hasLocalDraftOnly);



  const hasPreservedRenovationProject = Boolean(

    summary?.has_preserved_renovation_project ||

    summary?.project_id ||

    summary?.renovation_project_id,

  );



  const showEmptyState =

    !hasDossier &&

    !isDraftDossier &&

    !isReadyOrExportedDossier &&

    !hasLocalDraftOnly &&

    !hasOssConnection &&

    !hasExternalRenovation &&

    !hasPreservedRenovationProject;



  const showOssOnlyState =

    !hasDossier &&

    !isDraftDossier &&

    !isReadyOrExportedDossier &&

    !hasLocalDraftOnly &&

    hasOssConnection;



  const showExternalOnlyState =

    !hasDossier &&

    !isDraftDossier &&

    !isReadyOrExportedDossier &&

    !hasLocalDraftOnly &&

    !hasOssConnection &&

    hasExternalRenovation;



  const showPreservedProjectOnlyState =

    !hasDossier &&

    !isDraftDossier &&

    !isReadyOrExportedDossier &&

    !hasLocalDraftOnly &&

    !hasOssConnection &&

    !hasExternalRenovation &&

    hasPreservedRenovationProject;






  const hasCompletedDossier = Boolean(

    summary?.has_ready_for_export_dossier ||

    summary?.has_exported_dossier ||

    summary?.dossier_status === "READY_FOR_EXPORT" ||

    summary?.dossier_status === "EXPORTED",

  );



  const showCompletedDossierOverview =

    !isLenderManualDossier && hasCompletedDossier && !hasAgreedLenderResult;



  const showCompletedNoOss = showCompletedDossierOverview && !hasOssConnection;



  const showCompletedWithOss = showCompletedDossierOverview && hasOssConnection;






  const showNormalLoanResult =

    !isLenderManualDossier && hasCompletedDossier && hasAgreedLenderResult;



  const showLoanNoOss = showNormalLoanResult && !hasOssConnection;



  const showLoanWithOss = showNormalLoanResult && hasOssConnection;






  const showInvitedLoanNoOss =

    isLenderManualDossier && hasFinalLoanTerms && !hasOssConnection;



  const showInvitedLoanWithOss =

    isLenderManualDossier && hasFinalLoanTerms && hasOssConnection;






  const showInvitedWaitingState = isLenderManualDossier && !hasFinalLoanTerms;






  const handleDownloadCompletedDossier = React.useCallback(async () => {

    try {

      const dossierId = summary?.dossier_id;



      if (!dossierId) {

        return;

      }



      setIsDownloadingDossier(true);



      const accessToken = await getAccessTokenSilently();



      await prepareDossierDownload({

        dossierId,



        accessToken,

      });



      await downloadDossierZip({

        dossierId,



        accessToken,



        lang: "en",

      });

    } catch (error) {

      console.error("Could not download dossier:", error);

    } finally {

      setIsDownloadingDossier(false);

    }

  }, [summary?.dossier_id, getAccessTokenSilently]);






  const handleUpdateDossier = React.useCallback(async () => {




    if (isLenderManualDossier) {

      return;

    }



    try {

      const dossierId = summary?.dossier_id;



      if (!dossierId) {

        return;

      }



      const accessToken = await getAccessTokenSilently();



      const dossier = await getDossierDetail({

        accessToken,



        dossierId,

      });



      const draft = mapDossierDetailToDraft(dossier);



      const stepPath =

        summary?.current_step_path || "/hb/dossier/form/personal-info";



      const stepKeyMap = {

        "/hb/dossier/form/personal-info": "personal-info",



        "/hb/dossier/form/financial-info": "financial-info",



        "/hb/dossier/form/documents": "documents",



        "/hb/dossier/form/extracted-data": "extracted-data",



        "/hb/dossier/form/property-information": "property-information",



        "/hb/dossier/form/review-download": "review-download",

      };



      const draftMeta = {

        status: draft?.status || summary?.dossier_status || "DRAFT",



        dossier_id: draft?.dossier_id || null,



        dossier_code: draft?.dossier_code || null,



        dossier_applicant_id: draft?.dossier_applicant_id || null,



        current_step_key: stepKeyMap[stepPath] || "personal-info",



        current_step_label: summary?.current_stage_label || "Personal Info",



        current_step_path: stepPath,



        updated_at: new Date().toISOString(),

      };



      if (dossierStorageKey) localStorage.setItem(dossierStorageKey, JSON.stringify(draft));



      if (dossierMetaStorageKey) localStorage.setItem(dossierMetaStorageKey, JSON.stringify(draftMeta));



      setDraftStorageRevision((previous) => previous + 1);



      navigate(stepPath, {

        state: {

          fromUpdate: true,



          ts: Date.now(),

        },

      });

    } catch (error) {

      console.error("Could not prepare dossier update flow:", error);

    }

  }, [isLenderManualDossier, getAccessTokenSilently, navigate, summary, dossierStorageKey, dossierMetaStorageKey]);



  const handleLinkExternalRenovation = React.useCallback(async () => {

    const renovationId = summary?.external_renovation_id;

    const dossierId = summary?.dossier_id;



    if (!renovationId || !dossierId) {

      return;

    }



    try {

      const accessToken = await getAccessTokenSilently();



      const response = await fetch(

        `${API_BASE}/api/homeowner/external-renovations/${renovationId}/link-dossier/`,

        {

          method: "POST",



          headers: {

            Authorization: `Bearer ${accessToken}`,

            "Content-Type": "application/json",

          },



          body: JSON.stringify({

            dossier_id: dossierId,

          }),

        },

      );



      const data = await response.json().catch(() => ({}));



      if (!response.ok) {

        throw new Error(

          data?.detail ||

            "Could not link the external renovation to the dossier.",

        );

      }



      await refreshMe?.();

    } catch (error) {

      console.error("Could not link external renovation:", error);

    }

  }, [

    summary?.external_renovation_id,

    summary?.dossier_id,

    getAccessTokenSilently,

    refreshMe,

  ]);






  const handleOssSelect = React.useCallback(() => {




    if (isLenderManualDossier || isReadyOrExportedDossier) {

      navigate("/hb/oss/property-details", {

        state: {

          prefillFromDossier: true,



          dossierId: summary?.dossier_id || null,



          skipSituationStep: true,



          propertySituation: "HAS_PROPERTY",



          requestType: "RENOVATION",

        },

      });



      return;

    }



    setOssSituationModalOpen(true);

  }, [

    isLenderManualDossier,



    isReadyOrExportedDossier,



    navigate,



    summary?.dossier_id,

  ]);



  const handleExternalOss = React.useCallback(() => {

    navigate("/hb/renovation/external", {

      state: {

        dossierId: summary?.dossier_id || null,



        loanCaseId: summary?.loan_case_id || null,

      },

    });

  }, [navigate, summary?.dossier_id, summary?.loan_case_id]);






  const renderOssSelectionCard = () => (

    <DashboardActionCard

      eyebrow={t("cards.oss.eyebrow")}

      title={t("cards.oss.title")}

      description={t("cards.oss.description")}

      badge={t("cards.oss.badge")}

      buttonLabel={t("cards.oss.buttonLabel")}

      onClick={handleOssSelect}

      secondaryButtonLabel={t(

        "cards.oss.externalButtonLabel",



        "I already work with an OSS outside LEG-UP",

      )}

      onSecondaryClick={handleExternalOss}

      variant="secondary"

    />

  );



  const renderRenovationEntryCard = () => {

    if (hasOssConnection) {

      return (

        <DashboardRenovationCard

          summary={summary}

          onRefresh={refreshMe}

          onConnectOss={handleOssSelect}

        />

      );

    }



    if (hasExternalRenovation) {

      return (

        <DashboardExternalRenovationCard

          summary={summary}

          onCreateDossier={() => navigate("/hb/dossier")}

          onLinkDossier={handleLinkExternalRenovation}

          onManage={handleExternalOss}

          onRefresh={refreshMe}

        />

      );

    }



    if (hasPreservedRenovationProject) {

      return (

        <DashboardRenovationCard

          summary={summary}

          onRefresh={refreshMe}

          onConnectOss={handleOssSelect}

        />

      );

    }



    return renderOssSelectionCard();

  };






  React.useEffect(() => {

    if (isReadyOrExportedDossier) {

      if (dossierStorageKey) localStorage.removeItem(dossierStorageKey);



      if (dossierMetaStorageKey) localStorage.removeItem(dossierMetaStorageKey);



      setDraftStorageRevision((previous) => previous + 1);

    }

  }, [isReadyOrExportedDossier, dossierStorageKey, dossierMetaStorageKey]);






  const handleOssHasProperty = React.useCallback(() => {

    setOssSituationModalOpen(false);



    navigate("/hb/oss/property-details", {

      state: {

        prefillFromDossier: isLenderManualDossier || isReadyOrExportedDossier,



        dossierId: summary?.dossier_id || null,



        fromSituationModal: true,



        propertySituation: "HAS_PROPERTY",



        requestType: "RENOVATION",

      },

    });

  }, [

    isLenderManualDossier,



    isReadyOrExportedDossier,



    navigate,



    summary?.dossier_id,

  ]);






  const handleOssStillLooking = React.useCallback(() => {

    setOssSituationModalOpen(false);



    navigate("/hb/oss/partners/pre-purchase", {

      state: {

        dossierId: summary?.dossier_id || null,



        propertySituation: "SEARCHING",



        requestType: "PRE_PURCHASE",



        homebuyerCountry:

          profile?.country_of_residence ||

          profile?.country_of_property_purchase ||

          summary?.country_of_residence ||

          summary?.country_of_property_purchase ||

          summary?.country ||

          "",

      },

    });

  }, [navigate, profile, summary]);






  const heroSubtitle = React.useMemo(() => {

    const externalOssName =

      summary?.external_oss_name ||

      t("hero.externalOssFallback", {

        defaultValue: "your external OSS",

      });



    if (isLenderManualDossier && hasOssConnection) {

      return t("hero.subtitleDossierAndOss", {

        ossName: summary?.oss_name || t("hero.selectedOss"),

      });

    }



    if (isLenderManualDossier && hasExternalRenovation) {

      return t("hero.subtitleDossierAndExternalOss", {

        ossName: externalOssName,



        defaultValue:

          "Your financing dossier and external renovation information with {{ossName}} are connected.",

      });

    }



    if (isLenderManualDossier) {

      return t("hero.subtitleDossierNoOss");

    }



    if (!hasDossier && !hasLocalDraftOnly && hasOssConnection) {

      return t("hero.subtitleOssNoDossier", {

        ossName: summary?.oss_name || t("hero.selectedOss"),

      });

    }



    if (!hasDossier && !hasLocalDraftOnly && hasExternalRenovation) {

      return t("hero.subtitleExternalOssNoDossier", {

        ossName: externalOssName,



        defaultValue:

          "Your external renovation information with {{ossName}} is ready. Continue by creating your financing dossier.",

      });

    }



    if ((hasDossier || hasLocalDraftOnly) && hasOssConnection) {

      return t("hero.subtitleDossierAndOss", {

        ossName: summary?.oss_name || t("hero.selectedOss"),

      });

    }



    if ((hasDossier || hasLocalDraftOnly) && hasExternalRenovation) {

      return t("hero.subtitleDossierAndExternalOss", {

        ossName: externalOssName,



        defaultValue:

          "Your financing dossier and external renovation information with {{ossName}} are connected.",

      });

    }



    if (hasDossier || hasLocalDraftOnly) {

      return t("hero.subtitleDossierNoOss");

    }



    return t("hero.subtitleNoDossierNoOss");

  }, [

    isLenderManualDossier,



    hasDossier,



    hasLocalDraftOnly,



    hasOssConnection,



    hasExternalRenovation,



    summary?.oss_name,



    summary?.external_oss_name,



    t,

  ]);






  const handleCloseFirstLoginModal = React.useCallback(async () => {

    if (firstLoginModalClosing || firstLoginSeenRequestRef.current) {

      return;

    }



    firstLoginSeenRequestRef.current = true;



    const oldHeight = dashboardRef.current?.offsetHeight || 0;



    setDashboardLockedHeight(oldHeight);



    setFirstLoginModalClosing(true);



    firstLoginCloseTimerRef.current = window.setTimeout(() => {

      setFirstLoginModalOpen(false);



      requestAnimationFrame(() => {

        const normalHeight = underlayRef.current?.offsetHeight || oldHeight;



        setDashboardLockedHeight(normalHeight);



        window.setTimeout(() => {

          setDashboardLockedHeight(null);



          setFirstLoginModalClosing(false);

        }, 460);

      });

    }, 420);






    try {

      const accessToken = await getAccessTokenSilently();



      const response = await fetch(

        `${API_BASE}/api/homeowner/first-login-modal/seen/`,



        {

          method: "POST",



          headers: {

            Authorization: `Bearer ${accessToken}`,



            "Content-Type": "application/json",

          },

        },

      );



      if (!response.ok) {

        const data = await response.json().catch(() => ({}));



        throw new Error(

          data?.detail || "Could not mark first-login modal as seen.",

        );

      }






      await refreshMe?.();

    } catch (error) {

      console.error("Could not mark first-login modal as seen:", error);

    }

  }, [firstLoginModalClosing, getAccessTokenSilently, refreshMe]);






  React.useEffect(() => {

    return () => {

      if (firstLoginCloseTimerRef.current) {

        window.clearTimeout(firstLoginCloseTimerRef.current);

      }

    };

  }, []);






  const handleStartDossier = React.useCallback(() => {

    if (dossierStorageKey) localStorage.removeItem(dossierStorageKey);

    if (dossierMetaStorageKey) localStorage.removeItem(dossierMetaStorageKey);



    setDraftStorageRevision((previous) => previous + 1);



    navigate("/hb/dossier", {

      state: {

        startNewDossier: true,

        ts: Date.now(),

      },

    });

  }, [navigate, dossierStorageKey, dossierMetaStorageKey]);






  return (

    <div

      ref={dashboardRef}

      className={`hb-dashboard-view ${

        firstLoginModalOpen ? "hb-dashboard-view--first-login-open" : ""

      }`}

      style={

        dashboardLockedHeight

          ? {

              height: `${dashboardLockedHeight}px`,

            }

          : undefined

      }

    >

      <div ref={underlayRef} className="hb-dashboard-underlay">




        <div className="hb-dashboard-hero">

          <p className="hb-dashboard-hero__eyebrow">

            {hasDossier || hasLocalDraftOnly

              ? `${t("hero.welcomeBack")} ${firstName.toUpperCase()}`

              : `${t("hero.welcome")} ${firstName.toUpperCase()}`}

          </p>



          <h1 className="hb-dashboard-hero__title">{heroSubtitle}</h1>

        </div>






        {showEmptyState && (

          <div className="hb-dashboard-cards hb-dashboard-cards--equal">

            {renderOssSelectionCard()}



            <DashboardActionCard

              eyebrow={t("cards.dossier.eyebrow")}

              title={t("cards.dossier.title")}

              description={t("cards.dossier.description")}

              buttonLabel={t("cards.dossier.buttonLabel")}

              onClick={handleStartDossier}

              variant="primary"

            />

          </div>

        )}






        {showDraftState && (

          <div className="hb-dashboard-cards hb-dashboard-cards--draft">

            {renderRenovationEntryCard()}



            <DashboardDraftProgressCard

              draftMeta={draftMetaForCard}

              draftData={draftDataForCard}

              onContinue={

                isDraftDossier

                  ? handleUpdateDossier

                  : () => navigate(continueDraftPath)

              }

              onDelete={handleDeleteDraft}

              isDeleting={isDeletingDraft}

            />

          </div>

        )}






        {showOssOnlyState && (

          <div className="hb-dashboard-grid hb-dashboard-grid--full">

            <div className="hb-dashboard-grid__right">

              <DashboardRenovationCard

                summary={summary}

                onRefresh={refreshMe}

                onConnectOss={handleOssSelect}

              />

            </div>



            <div className="hb-dashboard-grid__left">

              <DashboardActionCard

                eyebrow={t("cards.loan.eyebrow")}

                title={t("cards.loan.title")}

                description={t("cards.loan.description")}

                buttonLabel={t("cards.loan.buttonLabel")}

                onClick={handleStartDossier}

                variant="primary"

              />

            </div>

          </div>

        )}






        {showExternalOnlyState && (

          <div className="hb-dashboard-grid hb-dashboard-grid--full">

            <div className="hb-dashboard-grid__right">

              <DashboardExternalRenovationCard

                summary={summary}

                onCreateDossier={() => navigate("/hb/dossier")}

                onLinkDossier={handleLinkExternalRenovation}

                onManage={handleExternalOss}

                onRefresh={refreshMe}

              />

            </div>



            <div className="hb-dashboard-grid__left">

              <DashboardActionCard

                eyebrow={t("cards.loan.eyebrow")}

                title={t("cards.loan.title")}

                description={t("cards.loan.description")}

                buttonLabel={t("cards.loan.buttonLabel")}

                onClick={() => navigate("/hb/dossier")}

                variant="primary"

              />

            </div>

          </div>

        )}



        {showPreservedProjectOnlyState && (

          <div className="hb-dashboard-grid hb-dashboard-grid--full">

            <div className="hb-dashboard-grid__right">

              <DashboardRenovationCard

                summary={summary}

                onRefresh={refreshMe}

                onConnectOss={handleOssSelect}

              />

            </div>



            <div className="hb-dashboard-grid__left">

              <DashboardActionCard

                eyebrow={t("cards.loan.eyebrow")}

                title={t("cards.loan.title")}

                description={t("cards.loan.description")}

                buttonLabel={t("cards.loan.buttonLabel")}

                onClick={handleStartDossier}

                variant="primary"

              />

            </div>

          </div>

        )}






        {showCompletedNoOss && (

          <div className="hb-dashboard-grid hb-dashboard-grid--application">

            <div

              className={

                hasExternalRenovation || hasPreservedRenovationProject

                  ? "hb-dashboard-grid__right"

                  : "hb-dashboard-grid__right " +

                    "hb-dashboard-grid__right--compact"

              }

            >

              {renderRenovationEntryCard()}

            </div>



            <div className="hb-dashboard-grid__left">

              <DashboardDossierOverviewCard

                summary={summary}

                onUpdate={handleUpdateDossier}

                onDownload={handleDownloadCompletedDossier}

                isDownloading={isDownloadingDossier}

                onDelete={handleDeleteReadyDossier}

                isDeleting={isDeletingReadyDossier}

                t={t}

              />

            </div>

          </div>

        )}






        {showCompletedWithOss && (

          <div className="hb-dashboard-grid hb-dashboard-grid--full">

            <div className="hb-dashboard-grid__right">

              <DashboardRenovationCard

                summary={summary}

                onRefresh={refreshMe}

                onConnectOss={handleOssSelect}

              />

            </div>



            <div className="hb-dashboard-grid__left">

              <DashboardDossierOverviewCard

                summary={summary}

                onUpdate={handleUpdateDossier}

                onDownload={handleDownloadCompletedDossier}

                isDownloading={isDownloadingDossier}

                onDelete={handleDeleteReadyDossier}

                isDeleting={isDeletingReadyDossier}

                t={t}

              />

            </div>

          </div>

        )}






        {showLoanNoOss && (

          <div className="hb-dashboard-grid hb-dashboard-grid--application">

            <div

              className={

                hasExternalRenovation || hasPreservedRenovationProject

                  ? "hb-dashboard-grid__right"

                  : "hb-dashboard-grid__right hb-dashboard-grid__right--compact"

              }

            >

              {renderRenovationEntryCard()}

            </div>



            <div className="hb-dashboard-grid__left">

              <DashboardLoanTermsCard summary={summary} />

            </div>

          </div>

        )}






        {showLoanWithOss && (

          <div className="hb-dashboard-grid hb-dashboard-grid--full">

            <div className="hb-dashboard-grid__right">

              <DashboardRenovationCard

                summary={summary}

                onRefresh={refreshMe}

                onConnectOss={handleOssSelect}

              />

            </div>



            <div className="hb-dashboard-grid__left">

              <DashboardLoanTermsCard summary={summary} />

            </div>

          </div>

        )}






        {showInvitedLoanNoOss && (

          <div className="hb-dashboard-grid hb-dashboard-grid--application">

            <div

              className={

                hasExternalRenovation || hasPreservedRenovationProject

                  ? "hb-dashboard-grid__right"

                  : "hb-dashboard-grid__right hb-dashboard-grid__right--compact"

              }

            >

              {renderRenovationEntryCard()}

            </div>



            <div className="hb-dashboard-grid__left">

              <DashboardLoanTermsCard summary={summary} />

            </div>

          </div>

        )}






        {showInvitedLoanWithOss && (

          <div className="hb-dashboard-grid hb-dashboard-grid--full">

            <div className="hb-dashboard-grid__right">

              <DashboardRenovationCard

                summary={summary}

                onRefresh={refreshMe}

                onConnectOss={handleOssSelect}

              />

            </div>



            <div className="hb-dashboard-grid__left">

              <DashboardLoanTermsCard summary={summary} />

            </div>

          </div>

        )}






        {showInvitedWaitingState && (

          <div className="hb-dashboard-cards hb-dashboard-cards--equal">

            {renderRenovationEntryCard()}



            <DashboardDossierOverviewCard

              summary={summary}

              onUpdate={null}

              onDownload={null}

              isDownloading={false}

              t={t}

            />

          </div>

        )}

      </div>






      {firstLoginModalOpen && (

        <FirstLoginInfoModal

          open={firstLoginModalOpen}

          isClosing={firstLoginModalClosing}

          onClose={handleCloseFirstLoginModal}

          t={t}

        />

      )}






      <OssSituationModal

        open={ossSituationModalOpen}

        onClose={() => setOssSituationModalOpen(false)}

        onHasProperty={handleOssHasProperty}

        onStillLooking={handleOssStillLooking}

      />

    </div>

  );

}
